import { useEffect } from 'react';
import { useErrorMonitor } from '@wix/fe-essentials-standalone';
import { getDataCapsule } from './data-capsule-adaptor';

const STORAGE_KEY = 'previously_viewed_templates';
const MAX_HISTORY = 10;

interface ViewedTemplateEntry {
  id: string;
  accountId: string;
}

interface ErrorReporter {
  captureException(exception: Error): void;
}

async function readHistory(
  errorMonitor?: ErrorReporter,
): Promise<ViewedTemplateEntry[]> {
  try {
    const capsule = getDataCapsule();
    const data = await capsule.getItem(STORAGE_KEY);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    errorMonitor?.captureException(err as Error);
    return [];
  }
}

async function writeHistory(
  entries: ViewedTemplateEntry[],
  errorMonitor?: ErrorReporter,
) {
  try {
    const capsule = getDataCapsule();
    await capsule.setItem(STORAGE_KEY, entries);
  } catch (err) {
    errorMonitor?.captureException(err as Error);
  }
}

export function usePreviouslyViewedTemplates(
  currentTemplateId: string,
  currentAccountId: string,
) {
  const errorMonitor = useErrorMonitor();

  useEffect(() => {
    (async () => {
      const history = await readHistory(errorMonitor);
      const last = history[history.length - 1];
      if (last?.id === currentTemplateId) {
        return;
      }
      const next: ViewedTemplateEntry[] = [
        ...history,
        { id: currentTemplateId, accountId: currentAccountId },
      ].slice(-MAX_HISTORY);
      await writeHistory(next, errorMonitor);
    })();
  }, [currentTemplateId, currentAccountId, errorMonitor]);
}

export async function getBackTemplateId(
  currentTemplateId: string,
  errorMonitor?: ErrorReporter,
): Promise<string | null> {
  const history = await readHistory(errorMonitor);
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].id !== currentTemplateId) {
      return history[i].id;
    }
  }
  return null;
}

export async function getLastViewedPartnerTemplateId(
  currentTemplateId: string,
  partnerAccountId: string,
  errorMonitor?: ErrorReporter,
): Promise<string | null> {
  const history = await readHistory(errorMonitor);
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (
      entry.id !== currentTemplateId &&
      entry.accountId === partnerAccountId
    ) {
      return entry.id;
    }
  }
  return null;
}
